import {
  CheckCircleTwoTone,
  LockOutlined,
  RollbackOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Alert, Form, Input } from 'antd';
import QueryString from 'query-string';
import React, { useEffect, useState } from 'react';
import { useLocalStorage } from 'react-use';
import { useRequest } from '../../api/hooks';
import { IS_POS } from '@/utils/env';
import PosButton from '@/components/PosButton';
import { playSound } from '@/utils/sound';
import { STORAGE_POS_LOGIN_HISTORY } from '@/utils/storage';
import { getAdminAvatar } from '@/utils/uiHelper';
import styles from './style.less';

interface LoginInfo {
  userName: string;
  avatar: string;
}

type Props = {
  mode?: 'page' | 'modal';
  onSuccess?: (loginInfo: LoginInfo) => void;
};

const STATUS_NOT_BIND_WECOM = 100001;
const WECOM_LOGIN_TYPE = 'wecom';

const Login: React.FC<Props> = ({ mode = 'page', onSuccess }: Props) => {
  const isModal = mode === 'modal';
  const isPOS = IS_POS;
  const [form] = Form.useForm();
  const [needBindAccount, setNeedBindAccount] = useState(false);
  const [needBindAccountId, setNeedBindAccountId] = useState('');
  const [loginHistory, setLoginHistory] = useLocalStorage<LoginInfo[]>(
    STORAGE_POS_LOGIN_HISTORY,
    []
  );
  const [accountMode, setAccountMode] = useState<'select' | 'input'>(
    isPOS && loginHistory.length ? 'select' : 'input'
  );
  const [selectedAccount, setSelectedAccount] = useState<LoginInfo>(null);

  const {
    loaded,
    loading,
    error,
    send: login,
    payload: loginInfo,
  } = useRequest<LoginInfo>(
    {
      method: 'post',
      url: `/auth/login`,
    },
    { manual: true }
  );
  const onFinish = values => {
    if (needBindAccount) {
      values.bindId = needBindAccountId;
      values.bindType = WECOM_LOGIN_TYPE;
    }
    login({ data: values }).then(loginInfo => {
      // 记录登录历史账户
      if (isPOS) {
        const history = loginHistory.slice(0, 3);
        const findIndex = history.findIndex(item => item.userName === loginInfo.userName);
        if (findIndex > -1) {
          history.splice(findIndex, 1);
        }
        history.unshift({ userName: loginInfo.userName, avatar: loginInfo.avatar });
        setLoginHistory(history);
      }

      if (loginInfo && onSuccess) {
        onSuccess(loginInfo);
      }
    });
  };

  const queryString = QueryString.parse(decodeURIComponent(location.search));

  const wxCode = queryString.code;

  useEffect(() => {
    if (loaded) {
      if (!isModal) {
        location.href = (queryString.return as string) || '/';
      }
    } else if (error) {
      if (error.code === STATUS_NOT_BIND_WECOM) {
        setNeedBindAccount(true);
        setNeedBindAccountId(error.msg);
      } else {
        // message.error(error.msg);
      }
    }
  }, [loaded, loginInfo, error, isModal]);

  useEffect(() => {
    if (wxCode) {
      login({
        data: {
          code: wxCode,
          type: WECOM_LOGIN_TYPE,
        },
      })
        .then()
        .catch();
    }
  }, [wxCode]);

  // if (wxCode) {
  //   return <>自动登录中，请稍等</>;
  // }

  return (
    <div
      className={`${styles.container} ${isPOS ? styles.pos : ''} ${isModal ? styles.modal : ''}`}>
      {isModal && (
        <div style={{ marginBottom: 40 }}>
          <Alert message={'当前会话已失效, 请重新登录'} />
        </div>
      )}
      {!isModal && (
        <div className={styles.brand}>
          <div className={styles.logo}>
            <img
              draggable={false}
              // src={`https://cdn.sjianke.com/assets/images/logo${isPOS ? '' : '-t'}.svg`}
              src={require('../../assets/logo.svg')}
              height={150}
              alt={'veeyung'}
            />
          </div>
        </div>
      )}
      {wxCode && !needBindAccount ? (
        <div>自动登录中, 请稍等</div>
      ) : (
        <div className={styles.main}>
          <Form
            className={styles.loginForm}
            form={form}
            initialValues={{ remember: true }}
            onFinish={onFinish}>
            {accountMode === 'select' && !selectedAccount ? (
              <>
                <div className={styles.historyList}>
                  <h4>点击选择最近登录帐号</h4>
                  {loginHistory.map(item => (
                    <div
                      key={item.userName}
                      className={`${styles.selectItem} ${styles.selectable}`}
                      onClick={() => {
                        playSound('click');
                        setSelectedAccount(item);
                        form.setFieldsValue({ username: item.userName });
                      }}>
                      {getAdminAvatar(item.avatar, '', { size: 28 })}
                      {item.userName}
                    </div>
                  ))}
                </div>
                <PosButton
                  size={'large'}
                  type={'primary'}
                  style={{ width: '100%' }}
                  onClick={() => {
                    setAccountMode('input');
                  }}>
                  登录其它账号
                </PosButton>
              </>
            ) : null}

            {accountMode === 'select' && selectedAccount && (
              <Form.Item name="username">
                <div className={styles.selectItemWrapper}>
                  <div className={styles.selectItem}>
                    {getAdminAvatar(selectedAccount.avatar, '', { size: 28 })}
                    {selectedAccount.userName}
                  </div>
                  <PosButton
                    onClick={() => {
                      setSelectedAccount(null);
                      form.setFieldsValue({ username: '' });
                    }}
                    size={'large'}
                    shape={'circle'}
                    ghost
                    icon={<RollbackOutlined />}
                  />
                </div>
              </Form.Item>
            )}
            {accountMode === 'input' && (
              <Form.Item
                name="username"
                rules={[{ required: true, message: '请输入用户名或手机号' }]}>
                <Input
                  prefix={<UserOutlined className="site-form-item-icon" />}
                  placeholder="请输入用户名或手机号"
                />
              </Form.Item>
            )}

            {(accountMode === 'input' || !!selectedAccount) && (
              <Form.Item name="password" rules={[{ required: true, message: '请输入密码' }]}>
                <Input
                  prefix={<LockOutlined className="site-form-item-icon" />}
                  type="password"
                  placeholder="请输入密码"
                />
              </Form.Item>
            )}

            {needBindAccount && (
              <Form.Item>
                <Alert
                  showIcon
                  description={
                    <>
                      登录后自动完成绑定，绑定后您可:
                      <div style={{ paddingTop: 5, lineHeight: '1.8em' }}>
                        <CheckCircleTwoTone twoToneColor={'#52c41a'} style={{ marginRight: 5 }} />
                        免密登录
                        <br />
                        <CheckCircleTwoTone twoToneColor={'#52c41a'} style={{ marginRight: 5 }} />
                        接收重要运营通知
                      </div>
                    </>
                  }
                  message={<>首次使用请通过账户密码登录</>}
                />
              </Form.Item>
            )}
            {(accountMode === 'input' || !!selectedAccount) && (
              <Form.Item>
                <PosButton
                  type="primary"
                  size={'large'}
                  loading={loading}
                  htmlType="submit"
                  className={styles.loginFormButton}>
                  登录
                </PosButton>
              </Form.Item>
            )}
          </Form>
        </div>
      )}
    </div>
  );
};
export default Login;
